import { useState, useMemo } from 'react';
import { useAdminTransactions, useAdminPayments } from '../services/adminTransactionService';
import { 
  CheckCircle, 
  XCircle, 
  Clock,
  ArrowUpRight,
  ArrowDownLeft
} from 'lucide-react';

/**
 * Custom hook for managing admin transactions
 * Handles fetching, filtering, and calculating transaction statistics
 */
export const useAdminTransactionsData = (initialFilters = {}) => {
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    date_from: '',
    date_to: '',
    ...initialFilters
  });

  // Transaction type options
  const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'payment', label: 'Payment' },
    { value: 'refund', label: 'Refund' },
    { value: 'payout', label: 'Payout' },
    { value: 'fee', label: 'Fee' }
  ];

  // Status options
  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' }
  ];

  // Clean filters to remove empty values
  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  // Add per_page to get all transactions
  const filtersWithPagination = {
    ...cleanFilters,
    per_page: 1000 // Get all transactions
  };

  // Fetch transactions (with fallback to payments)
  const { data: transactionsResponse, isLoading, error } = useAdminTransactions(filtersWithPagination);
  
  // Fallback: use payments as transactions if transactions endpoint doesn't exist
  const { data: paymentsResponse } = useAdminPayments(filtersWithPagination, {
    enabled: !transactionsResponse && !error
  });

  // Extract transactions from response
  const transactions = useMemo(() => {
    return transactionsResponse?.data?.data || 
           transactionsResponse?.data || 
           paymentsResponse?.data?.data || 
           paymentsResponse?.data || 
           [];
  }, [transactionsResponse, paymentsResponse]);

  // Get status configuration
  const getStatusConfig = (status) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Failed' }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  // Get transaction type icon
  const getTypeIcon = (type) => {
    if (type === 'refund' || type === 'payout') {
      return <ArrowDownLeft className="w-4 h-4 text-red-500" />;
    }
    return <ArrowUpRight className="w-4 h-4 text-green-500" />;
  };

  // Calculate statistics
  const statistics = useMemo(() => {
    const totalInflow = transactions
      .filter(t => (t.type === 'payment' || !t.type) && t.status === 'completed')
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

    const totalOutflow = transactions
      .filter(t => (t.type === 'refund' || t.type === 'payout') && t.status === 'completed')
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

    const netAmount = totalInflow - totalOutflow;

    return {
      totalInflow,
      totalOutflow,
      netAmount,
      totalTransactions: transactions.length
    };
  }, [transactions]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle search
  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(transaction => 
      transaction.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.payment_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.booking?.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.booking?.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.client?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return {
    // Data
    transactions,
    isLoading,
    error,
    
    // Filters
    filters,
    cleanFilters,
    typeOptions,
    statusOptions,
    handleFilterChange,
    
    // Utilities
    getStatusConfig,
    getTypeIcon,
    handleSearch,
    
    // Statistics
    statistics
  };
};

